<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { X } from 'phosphor-svelte';
  import type { FilenameWhitelist } from '$lib/models/ccr/types';

  const MIN_PREFIX_LENGTH = 8;
  const MAX_PREFIX_LENGTH = 23;

  function isValidPrefixString(value: string | null | undefined): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return trimmed.length >= MIN_PREFIX_LENGTH && trimmed.length <= MAX_PREFIX_LENGTH;
  }

  function getValidationError(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.length < MIN_PREFIX_LENGTH) return `Prefix must be at least ${MIN_PREFIX_LENGTH} characters`;
    if (trimmed.length > MAX_PREFIX_LENGTH) return `Prefix must be at most ${MAX_PREFIX_LENGTH} characters`;
    return null;
  }

  export let isOpen = false;
  export let prefixes: FilenameWhitelist[] = [];
  export let cardType: string;
  export let prefixSuggestion: string = '';

  const dispatch = createEventDispatcher();

  let newPrefix = '';
  let localPrefixes = [...prefixes];

  $: validationError = getValidationError(newPrefix);

  $: if (isOpen) {
    localPrefixes = [...prefixes];
    const suggestionAlreadyExists = prefixes.some(p => p.filenamePrefix === prefixSuggestion);
    newPrefix = (prefixSuggestion && !suggestionAlreadyExists) ? prefixSuggestion : '';
  }

  function addPrefix() {
    if (isValidPrefixString(newPrefix)) {
      localPrefixes = [...localPrefixes, {
        id: crypto.randomUUID(),
        creditCardType: cardType as any,
        filenamePrefix: newPrefix.trim()
      }];
      newPrefix = '';
    }
  }

  function removePrefix(id: string) {
    localPrefixes = localPrefixes.filter(p => p.id !== id);
  }

  function handleClose() {
    dispatch('close', localPrefixes);
    isOpen = false;
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addPrefix();
    }
  }
</script>

{#if isOpen}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div
      class="fixed inset-0 bg-black bg-opacity-50"
      on:click={handleClose}
      on:keydown={(e) => e.key === 'Enter' && handleClose()}
      role="button"
      tabindex="0"
      aria-label="Close modal"
    ></div>

    <div class="relative bg-white rounded-lg shadow-xl" style="width: 700px;">
      <div class="p-6 pb-0">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">Prefixes</h3>
          <button
            class="text-gray-400 hover:text-gray-600"
            on:click={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        <p class="text-sm text-gray-600 mb-4">Add prefixes to your {cardType} sftp connection.</p>

        <div class="border-t border-gray-200 mb-6"></div>

        <div class="bg-gray-50 rounded-lg p-6 mb-3">
          <div class="space-y-3 min-h-[150px] max-h-64 overflow-y-auto">
            {#each localPrefixes as prefix (prefix.id)}
              <div class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                <span class="text-sm text-gray-700">{prefix.filenamePrefix}</span>
                <button
                  class="p-1 text-red-400 hover:text-red-600"
                  on:click={() => removePrefix(prefix.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            {/each}
          </div>
        </div>

        <div class="bg-gray-50 rounded-lg p-5 mb-6">
          <div class="flex items-center gap-3">
            <input
              type="text"
              value={newPrefix}
              on:input={(e) => newPrefix = e.currentTarget.value}
              on:keydown={handleKeydown}
              placeholder="Type Prefix..."
              class="flex-1 px-3 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none text-sm placeholder-gray-400"
            />
            <button
              class="px-4 py-3 text-gray-700 hover:text-gray-800 transition-colors flex items-center gap-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              on:click={addPrefix}
              disabled={!isValidPrefixString(newPrefix)}
            >
              <span>+</span>
              <span>Add</span>
            </button>
          </div>
          {#if validationError}
            <p class="text-sm text-red-500 mt-2">{validationError}</p>
          {/if}
        </div>

        <div class="border-t border-gray-200"></div>

        <div class="flex justify-end p-4">
          <button
            class="px-6 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded transition-colors"
            on:click={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}